import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { DiscountTypes, IPSSDiscount } from "@/interfaces/store/IBooking";
import BookingModule from "@/store/modules/booking";
import { AccountingState } from "@/types/db/accounting";
import {
  CodeTransactionType,
  IProductSlotState,
} from "@/types/db/product-slot-state";
import dayjs from "dayjs";
import { isPssPaid } from "./product-slot-state";
import { LogService } from "@/services/logging";
import { cloneDeep } from "lodash";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import { removeGenericDiscountAccountings, restToPay } from "./new-accountings";
import { getPSSValue } from "../helpers/product-slot-state";
import { isBookingInDexie } from "../helpers/booking";

export async function buildPSSDiscountArray() {
  // This function build an array of IPSSDiscount based on:
  // The type of the discount (Percentage or Fixed value)
  // The value of the discount (50%, 10 CHF)
  // The coverage of the discount (On All Order or On Specific Tickets)
  // Whether pss has discount already applied on it or not
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  let discountPercentage = 0;
  const discountTracking = BookingModule.selectedBookingDiscountTrack;

  let bookingPSS: IProductSlotState[] = [];
  // TODO Hamza: in case of EditOrder we do not consider paid pss
  const isEditOrder = await isBookingInDexie(booking.uuid);
  if (isEditOrder) {
    booking.pssList.forEach((pss) => {
      if (!isPssPaid(pss)) {
        bookingPSS.push(pss);
      }
    });
  } else {
    bookingPSS = booking.pssList;
  }

  const pssDiscounts: IPSSDiscount[] = [];

  if (discountTracking?.type === 1) {
    // this is percentage discount
    discountPercentage = discountTracking.value;

    const discountPrices =
      BookingModule.selectedBookingDiscountTrack?.pricesIds;

    // const pssDiscounts: IPSSDiscount[] = [];

    // 1- if we are in on all order discount type then consider all pss of the booking
    // 2- if we are in a discount of only specific tickets then consider only the pss of those tickets
    for (const pss of bookingPSS) {
      if (!discountPrices) {
        pss._discountValue = 0;
        continue;
      }

      if (
        (discountPrices.findIndex((priceId) => priceId === pss.priceId) > -1 &&
          !discountTracking?.isOnAllOrder) ||
        discountTracking?.isOnAllOrder
      ) {
        // so this pss is concerned by the discount
        pss._discountValue = (getPSSValue(pss) * discountPercentage) / 100;
        const applyTime = dayjs().toISOString();
        pssDiscounts.push({
          pssId: pss._id,
          priceId: pss.priceId,
          pssUUID: pss.uuid,
          pssOriginalPriceValue: getPSSValue(pss),
          pssNewPriceValue: getPSSValue(pss) - pss._discountValue,
          applyTime: applyTime,
        });
      } else pss._discountValue = 0;
    }
  } else if (discountTracking?.type === 2) {
    // this is fixed value discount
    // // lets calculate the percentage of the discount compared to the total amount of the booking
    // discountPercentage =
    //   (discountTracking.value * 100 * 100) / bookingTotalAmount();
    // // Why multiplied twice by 100? First one to cenvert the value from CHF to cents, second one is the rule

    // now lets loop thru pss and make them fully discounted until we reach the discount amount decided by user

    const discountFixedAmount = discountTracking.value * 100;
    let remainingDiscountAmount = discountFixedAmount;
    for (const pss of bookingPSS) {
      // keep in mind that a pss can have an already applied promocode that don't show in pssList yet but it is shown in BookingModule.promocodeDiscountValues
      // Lets now check if the current pss is already considered in an already applied promocode
      let pssPromocodeDiscount = 0;

      // SINCE the new logic (promocodes rewrite) generic discount is always applied first, then comes any code discount
      // so we set pssPromocodeDiscounts to an empty array and keep the rest of the code as it is
      const pssPromocodeDiscounts: {
        pssUuid: string;
        promocodeDiscountValue: number;
        promoCodeId: number | null;
        codeTransactionType: CodeTransactionType;
      }[] = []; /*BookingModule.promocodeDiscountValues*/
      const pssPromocodeDiscountObj = pssPromocodeDiscounts.find(
        (it) => it.pssUuid === pss.uuid
      );
      if (
        pssPromocodeDiscountObj &&
        pssPromocodeDiscountObj.promocodeDiscountValue
      ) {
        pssPromocodeDiscount = pssPromocodeDiscountObj.promocodeDiscountValue;
      }

      if (getPSSValue(pss) - pssPromocodeDiscount <= remainingDiscountAmount) {
        // lets make this pss fully discounted
        pss._discountValue = getPSSValue(pss) - pssPromocodeDiscount;
        // decrease remainingDiscountAmount
        remainingDiscountAmount =
          remainingDiscountAmount - getPSSValue(pss) - pssPromocodeDiscount;
      } else {
        // so here (pss price value - pssPromocodeDiscount) is greater than what we still have as a discount amount to use
        // we will make a normal discount, not a full one
        pss._discountValue = remainingDiscountAmount;
        remainingDiscountAmount = 0;
      }
      const applyTime = dayjs().toISOString();
      pssDiscounts.push({
        pssId: pss._id,
        priceId: pss.priceId,
        pssUUID: pss.uuid,
        pssOriginalPriceValue: getPSSValue(pss) - pssPromocodeDiscount,
        pssNewPriceValue:
          getPSSValue(pss) - pssPromocodeDiscount - pss._discountValue,
        categoryId: pss.categoryId,
        applyTime: applyTime,
      });
    }
  }
  return pssDiscounts;
}

export async function updateDiscount(
  priceId: number,
  createdPSSList?: IProductSlotState[]
) {
  // If the booking has a discount applied and the user has created new pss then we need to update the discount tracking object based on whether the discount is on whole order or on specific tickets
  // If the booking has a discount applied and the user has removed some pss then we need to update the discount tracking object based on whether the discount is on whole order or on specific tickets
  // If the parameter createdPSSList is supplied, then we are in the case where user has selected more tickets
  // If the parameter createdPSSList is not supplied, then we are in the case where user has removed some tickets
  const booking = BookingModule.selectedBooking;

  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const bookingDiscount = BookingModule.selectedBookingDiscountTrack;
  if (bookingDiscount && bookingDiscount.isApplied) {
    if (createdPSSList) {
      // So we are in the case of new pss have been created

      // 1- Check if we need to add this price to discountTracking.pricesIds array if we are in an order discount,
      // if we are in specific ticket discount then don't update discountTracking.pricesIds
      const discountPrices =
        BookingModule.selectedBookingDiscountTrack?.pricesIds;

      // Remember: do not consider the price not concerned by the discount if we are in a specific ticket discount
      const isNewPrice =
        (discountPrices?.findIndex((Id) => Id === priceId) === -1 &&
          bookingDiscount.isOnAllOrder) ||
        false;

      if (isNewPrice) {
        const data = {
          BookingUUID: BookingModule.selectedBookingUUID,
          pricesIds: (discountPrices || []).concat([priceId]),
        };
        BookingModule.updateDiscountPrices(data);
      }

      const pssDiscounts = await buildPSSDiscountArray();

      const pssDiscountsData = {
        BookingUUID: BookingModule.selectedBookingUUID,
        pssDiscounts,
      };

      BookingModule.updatePSSDiscount(pssDiscountsData);
    }

    if (!createdPSSList) {
      // So we are in the case of some pss have been removed from the booking

      // Rebuild discountTracking.pssDiscounts array using existing pssList of the booking
      // (Why not just remove those pss from discountTracking.pssDiscount? Because calculation may change in the case of Fixed Discount Value applied on all order)

      let pssList: IProductSlotState[] = [];
      // TODO Hamza: in case of EditOrder we do not consider paid pss
      const isEditOrder = await isBookingInDexie(booking.uuid);
      if (isEditOrder) {
        booking.pssList.forEach((pss) => {
          if (!isPssPaid(pss)) {
            pssList.push(pss);
          }
        });
      } else {
        pssList = booking.pssList;
      }
      if (!pssList || pssList.length === 0) {
        // No pss are left for this booking lets remove the discount tracking
        BookingModule.removeDiscountTracking(BookingModule.selectedBookingUUID);
      } else {
        // Rebuild discountTracking.pssDiscounts array using existing pssList of the booking
        const pssDiscounts = await buildPSSDiscountArray();
        BookingModule.updatePSSDiscount({
          BookingUUID: BookingModule.selectedBookingUUID,
          pssDiscounts,
        });
      }
    }
  }
}

export function bookingTotalAmount(): number {
  let sum = 0;
  BookingModule.selectedBooking?.pssList
    .map((pss) => getPSSValue(pss))
    .forEach((priceValue) => (sum = sum + priceValue));
  return sum;
}

/**
 * 0- Remove the selected booking discount tracking object
 * 1- We edit pss.priceValue or pss.accountingValue to be full price value, and pss._discountValue to be 0
 * 2- If the amount is confirmed (payment applied) then the booking now have two accountings object
 * we will remove the discount accounting and update the accEntry.value of the other accounting
 * 3- Increase the confirmed amount to the full booking amount
 */
export async function removeDiscount() {
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const bookingUuid = booking.uuid;
  if (!bookingUuid) {
    throw new Error(PSBErrors.HasNoUUID);
  }

  // 0- remove discount tracking object
  BookingModule.removeDiscountTracking(bookingUuid);

  // 1- edit pss.priceValue or pss.accountingValue to the full price value & pss._discountValue to zero
  // if the booking has no accountings then pss.priceValue still has full price value (not decreased yet)
  const updatedBooking = Object.assign({}, booking);
  const pssList = updatedBooking.pssList;
  for (const pss of pssList) {
    pss._discountValue = 0;
  }

  // 2- remove the discount accounting and update the accEntry.value of the other accounting if it is not already completed
  const bookingPayment = BookingModule.selectedBookingPayment;
  if (!bookingPayment) {
    throw new Error("Booking has no payment chosen");
  }

  // We do not actually need to remove a discount accounting when we remove the discount
  // because we don't create discountAccountings when we apply the discount
  // const newAcc = updatedBooking.accountings.filter(
  //   (acc) =>
  //     acc.paymentMethod !== PaymentMethods.METHODS_POS_DISCOUNT &&
  //     acc.state !== AccountingState.Completed
  // );
  // updatedBooking.accountings = newAcc;
  for (const acc of updatedBooking.accountings) {
    if (
      acc.state === AccountingState.Completed ||
      acc.promoCodeId ||
      acc.promoCodeUuid ||
      acc.promocode
    ) {
      // Never touch a completed accounting
      // And never change the value of payment code because there is a logic behind that decided the value
      continue;
    }
    for (const accEntry of acc.accountingEntriesList) {
      const pss = updatedBooking.pssList.find(
        (pss) => pss.uuid === accEntry.productSlotStateUuid
      );
      if (pss) {
        accEntry.value = getPSSValue(pss);
      }
    }
  }
  BookingModule.updateBooking(updatedBooking);
  LogService.info(`Discount was removed ${bookingUuid}`, { params: booking });

  // 3- increase the confirmed amount, if there is any, to the full booking amount
  const confirmedAmount =
    BookingModule.bookingsAmountsTrack[bookingUuid].confirmedAmounts[0];
  if (confirmedAmount) {
    confirmedAmount.confirmedAmount = BookingModule.bookingTotal / 100;
    BookingModule.updateConfirmedAmounts({
      bookingUUID: bookingUuid,
      confirmedAmounts: [confirmedAmount],
    });
  }

  BookingModule.setSplitPaymentAmount({
    BookingUUID: BookingModule.selectedBookingUUID,
    splitAmount: restToPay.value / 100 /*Math.min(
      BookingModule.pssSumFinaValues / 100,
      Math.abs(BookingModule.leftToPayAmount / 100)
    )*/,
  });
}

export function initiateDiscount() {
  const selectedBooking = BookingModule.selectedBooking;
  if (!selectedBooking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  BookingModule.addDiscountTracking({
    BookingUUID: BookingModule.selectedBookingUUID,
    track: {
      isApplied: false,
      type: DiscountTypes.Percentage_Discount,
      value: 5,
      isOnAllOrder: true,
      pricesIds: [
        ...new Set(selectedBooking.pssList.map((pss) => pss.priceId)),
      ],
      pssDiscounts: [],
      discountTotalValue: 0, // will be calculated when the discount is applied
    },
  });
}

export async function refreshDiscount() {
  // First of all, lets remove any generic discount accounting already generated and not completed
  const bookingCopy = cloneDeep(
    BookingModule.selectedBooking as IProductSlotBooking
  );
  removeGenericDiscountAccountings(bookingCopy);
  BookingModule.updateBooking(bookingCopy);
  await removeDiscount();
  initiateDiscount();
}
